import { AsyncState } from 'widgets-for-react'
import { SingleValidatorResult, Validator, ValidatorResult, singleValidatorResultBuilders } from '../../../../../../Shared/Frontend/validators/validatorExpr'
import type { AnchorProps, ModernBlocksBaseClassNames } from '../../modernBlocks.state'
import { ZipCode } from '../../../utils/ZipcodeChecker'
import { ExpandableItem } from '../expandableBlock/expandableBlock.state'
import { FieldValidatorsBuilder, buildValidator, validatorBuilders } from '../../../../../../Shared/Frontend/validators/validator'
import { isNonBlacklistedPhone } from './utils'
import { t } from 'i18next'
import { List } from 'immutable'
import { possibleSalutations } from '../donationFormBlock/donationFormBlock.state'

export interface PredefinedAmount {
  amount: number
  button: string
  explainerText?: string
}

export interface FormInfo {
  drupalformId: number

  successUrl: string
  failedUrl: string
  cancelUrl: string
}

export interface ProductApiResponseParsed {
  customTitle?: string
  formInfo: FormInfo
  productForm: ProductForm
  submitButton: string

  productId: string
}

export type ProductForm =
  | ProductSale
  | ProductSaleDonation
  | ProductPostageCosts

export type ProductFormAll =
  & Omit<ProductSale, "kind">
  & Omit<ProductSaleDonation, "kind">
  & Omit<ProductPostageCosts, "kind">

export interface ProductSaleDonation {
  kind: "product_sale_donation"
  minimumAmount: number
  maximumAmount: number
  selectedIndex?: number
  manualAmount?: number
  amounts: PredefinedAmount[]

  price: number
  wantToDonate?: boolean
}

export interface ProductPostageCosts {
  kind: "product_package_costs"
  price: number
  sponsor?: boolean
}

export interface ProductSale {
  kind: "product_sale"
  price: number
}

export type possibleSalutationsType = typeof possibleSalutations[number]
export interface FormInput {
  email: string
  salutation?: possibleSalutationsType
  firstName: string
  insertion: string
  lastName: string
  postalCode: string
  houseNumber: string
  houseNumberAddition: string
  invalidHousenumberPostcodeCombination?: boolean
  street: string
  city: string
  phone: string
  acceptNewsLetter: boolean
}

export const initialFormInput : FormInput = ({
  email: '',
  salutation: 'Zeg_ik_liever_niet',
  firstName: '',
  insertion: '',
  lastName: '',
  postalCode: '',
  houseNumber: '',
  houseNumberAddition: '',
  street: '',
  city: '',
  phone: '',
  acceptNewsLetter: false,
})

type ProductFormBlockProps = {
  blockIndex: number
  // formPreset: string
  text?: string
  submitState: AsyncState<{}>
  title: string
  expandableItems: ExpandableItem[]
} & AnchorProps

export type ProductFormState = ProductFormBlockProps & {
  input: FormInput
  validatorInput?: Partial<FormInput>
  donationFormLoader: AsyncState<ProductApiResponseParsed>
  validator: Validator<Partial<FormInput>, Partial<FormInput>>
  productValidator: Validator<Partial<ProductForm>, Partial<ProductForm>>
  validatorResult: ValidatorResult
  productValidatorResult: ValidatorResult
  zipCodeLoader: AsyncState<ZipCode.CheckResponse>
  recaptcha: string
}

export type DonationFormBLockClassNames = ModernBlocksBaseClassNames &
  Partial<{
    // INSERT BLOCK SPECIFIC CLASSNAMES HERE!
  }>

export const zipCodeUpdaters: List<Partial<keyof FormInput>> = List([
  'houseNumber',
  'postalCode'
])

export const numericValues: List<Partial<keyof FormInput>> = List([
  'houseNumber'
])

const productFormValidator: Partial<FieldValidatorsBuilder<Partial<ProductForm>, Partial<ProductForm>>> = {
  sponsor: [
    b => b.fromPromise(async (i, o) => {
      if (i.kind !== "product_package_costs") return true

      return i.sponsor !== undefined
    }),
    validatorBuilders.boolean("sponsor", () => t("ProductForm:Error.sponsor"))
  ],
  wantToDonate: [
    b => b.fromPromise(async (i, o) => {
      if (i.kind !== "product_sale_donation") return true

      return i.wantToDonate !== undefined
    }),
    validatorBuilders.boolean("wantToDonate", () => t("ProductForm:Error.wantToDonate"))
  ],
  manualAmount: [
    b => b.fromPromise(async (i,o) => {
      if (i.kind !== "product_sale_donation") return true
      if (!i.wantToDonate) return true
      if (i.selectedIndex !== undefined) return true

      return i.manualAmount! >= i.minimumAmount! && i.manualAmount! <= i.maximumAmount!
    }), validatorBuilders.number("manualAmount", () => "error")
  ]
}

const singleDonationValidations: Partial<FieldValidatorsBuilder<Partial<FormInput>, Partial<FormInput>>> = {
  email: [b => b.isEmail(), validatorBuilders.string("email", () => t("DonationBlock:DonationForm.Error_email_incorrect"))],
  firstName: [b => b.notEmpty(), validatorBuilders.string("firstName", () => t("DonationBlock:DonationForm.Error_firstName"))],
  lastName: [b => b.notEmpty(), validatorBuilders.string("lastName", () => t("DonationBlock:DonationForm.Error_lastName"))],
  postalCode: [b => b.isPostalCode(), validatorBuilders.string("postalCode", () => t("DonationBlock:DonationForm.Error_postalCode"))],
  houseNumber: [b => b.isNumeric(), validatorBuilders.string("houseNumber", () => t("DonationBlock:DonationForm.Error_houseNumber"))],
  phone: [b => (b
    .fromPromise(async (i, _) => i.phone == undefined || i.phone == "")
    .or(b
      .fromPromise(async (_, phone) => isNonBlacklistedPhone(phone))
      .properAnd(b.isPhone())
    )
  ), validatorBuilders.string('phone', () => t("DonationBlock:DonationForm.Error_phone"))]
}

export const validators = {
  emptyValidator: buildValidator<Partial<FormInput>>({}),
  singleDonationValidator: buildValidator<Partial<FormInput>>(singleDonationValidations),
  productFormValidator: buildValidator<Partial<ProductForm>>(productFormValidator),
}

export const getValidation = (state: ProductFormState, field: keyof FormInput): SingleValidatorResult =>
  state.validatorInput?.[field] !== undefined ? state.validatorResult.path(field) : singleValidatorResultBuilders.validating(field)

export const getProductValidation = (state: ProductFormState, field: keyof Partial<ProductFormAll>): SingleValidatorResult =>
  state.productValidatorResult.path(field)
